import { api } from './client';

export interface SpaceOption {
  id: number;
  slug: string;
  name: string;
}

export interface HelpdeskAccount {
  id: number;
  primary_space_id: number;
  name: string;
  email: string;
  language: string;
  signature_default: string | null;
  ai_enabled: number;
  mode: string;
  active: number;
  oauth_connected: number;
}

export interface HelpdeskAiAgent {
  id: number;
  space_id: number | null;
  slug: string;
  name: string;
  provider: string;
  model: string;
  signature: string | null;
  system_prompt_extra: string | null;
  temperature: string;
  max_tokens: number;
  daily_cost_limit_usd: string;
  autonomy_mode: string;
  autopilot_min_confidence: string;
  active: number;
}

export interface HelpdeskRule {
  id: number;
  space_id: number;
  kind: string;
  title: string;
  slug: string;
  body_html: string | null;
  body_text: string | null;
  sort_order: number;
  active: number;
}

export interface HelpdeskTag {
  id: number;
  slug: string;
  name: string;
  color: string;
  description: string | null;
  sort_order: number;
  ai_autopilot: string;
  active: number;
  usage_count: number;
}

export interface HelpdeskTemplate {
  id: number;
  account_id: number | null;
  account_name: string | null;
  code: string;
  language: string;
  subject: string;
  body_plain: string;
  enabled: number;
  updated_at: string;
}

export interface TemplateVariable {
  key: string;
  label: string;
  sample: string;
}

export interface TemplateListResult {
  templates: HelpdeskTemplate[];
  variables: TemplateVariable[];
}

export interface TemplatePreview {
  subjectRendered: string;
  bodyHtmlRendered: string;
  fullEmailHtml: string;
}

export interface HelpdeskAccessUser {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role_keys: string[];
  has_access: boolean;
}

const base = '/api/v1/helpdesk/admin';

export const helpdeskAdminApi = {
  listSpaces: () => api.get<SpaceOption[]>(`${base}/spaces`),

  listAccounts: () => api.get<HelpdeskAccount[]>(`${base}/accounts`),
  createAccount: (body: Record<string, unknown>) => api.post<{ id: number }>(`${base}/accounts`, body),
  updateAccount: (id: number, body: Record<string, unknown>) => api.patch<{ id: number }>(`${base}/accounts/${id}`, body),
  startGmailOAuth: (id: number) => api.post<{ authUrl: string }>(`${base}/accounts/${id}/oauth/gmail/start`, {}),
  startGmailWatch: (id: number) => api.post<{ historyId: string; expiration: string }>(`${base}/accounts/${id}/gmail/watch`, {}),
  initialGmailImport: (id: number, limit = 20) => api.post<{ imported: number }>(`${base}/accounts/${id}/gmail/initial-import?limit=${limit}`, {}),

  listAiAgents: () => api.get<HelpdeskAiAgent[]>(`${base}/ai-agents`),
  createAiAgent: (body: Record<string, unknown>) => api.post<{ id: number }>(`${base}/ai-agents`, body),
  updateAiAgent: (id: number, body: Record<string, unknown>) => api.patch<{ id: number }>(`${base}/ai-agents/${id}`, body),

  listRules: (spaceId?: number, kind?: string) => {
    const q = new URLSearchParams();
    if (spaceId) q.set('spaceId', String(spaceId));
    if (kind) q.set('kind', kind);
    const qs = q.toString();
    return api.get<HelpdeskRule[]>(`${base}/rules${qs ? `?${qs}` : ''}`);
  },
  createRule: (body: Record<string, unknown>) => api.post<{ id: number }>(`${base}/rules`, body),
  updateRule: (id: number, body: Record<string, unknown>) => api.patch<{ id: number }>(`${base}/rules/${id}`, body),
  deleteRule: (id: number) => api.delete<{ id: number }>(`${base}/rules/${id}`),

  listTags: () => api.get<HelpdeskTag[]>(`${base}/tags`),
  createTag: (body: Record<string, unknown>) => api.post<{ id: number }>(`${base}/tags`, body),
  updateTag: (id: number, body: Record<string, unknown>) => api.patch<{ id: number }>(`${base}/tags/${id}`, body),
  deleteTag: (id: number) => api.delete<{ id: number }>(`${base}/tags/${id}`),

  listTemplates: () => api.get<TemplateListResult>(`${base}/templates`),
  createTemplate: (body: Record<string, unknown>) => api.post<{ id: number }>(`${base}/templates`, body),
  updateTemplate: (id: number, body: Record<string, unknown>) => api.patch<{ id: number }>(`${base}/templates/${id}`, body),
  previewTemplate: (body: Record<string, unknown>) => api.post<TemplatePreview>(`${base}/templates/preview`, body),

  listAccessUsers: () => api.get<HelpdeskAccessUser[]>(`${base}/users`),
};
